import React, { useState, useEffect } from 'react';
import { X, User, Mail, Calendar, MapPin, Phone, Briefcase } from 'lucide-react';
import api from '../utils/api';


const UserProfileModal = ({ isOpen, onClose, userId, userName }) => {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && userId) {
      loadUserProfile();
    }
  }, [isOpen, userId]);

  const loadUserProfile = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.get(`/users/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setUserProfile(response.data);
    } catch (error) {
      console.error('Error loading user profile:', error);
      setError('Failed to load user profile');
      
      // Fallback to basic user info if available
      if (userName) {
        setUserProfile({
          id: userId,
          name: userName,
          email: '',
          avatar_color: '#6B46C1'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-500 to-blue-500">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <User className="w-6 h-6 text-white" />
              <h2 className="text-xl font-semibold text-white">User Profile</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-purple-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading profile...</p>
              </div>
            </div>
          ) : error && !userProfile ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <p className="text-red-600 font-medium">Failed to load profile</p>
              <p className="text-gray-500 text-sm mt-2">{error}</p>
            </div>
          ) : userProfile ? (
            <div className="space-y-6">
              {/* Avatar and Basic Info */}
              <div className="text-center">
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4"
                  style={{ backgroundColor: userProfile.avatar_color || '#6B46C1' }}
                >
                  {getInitials(userProfile.name)}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-1">
                  {userProfile.name || 'Unknown User'}
                </h3>
                {userProfile.title && (
                  <p className="text-purple-600 font-medium mb-2">{userProfile.title}</p>
                )}
                {userProfile.department && (
                  <p className="text-gray-600 text-sm">{userProfile.department}</p>
                )}
              </div>

              {/* Contact Information */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                  Contact Information
                </h4>
                
                {userProfile.email && (
                  <div className="flex items-center space-x-3 text-gray-600">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Email</p>
                      <p className="text-sm">{userProfile.email}</p>
                    </div>
                  </div>
                )}

                {userProfile.phone && (
                  <div className="flex items-center space-x-3 text-gray-600">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Phone</p>
                      <p className="text-sm">{userProfile.phone}</p>
                    </div>
                  </div>
                )}

                {userProfile.location && (
                  <div className="flex items-center space-x-3 text-gray-600">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Location</p>
                      <p className="text-sm">{userProfile.location}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Work Information */}
              {(userProfile.title || userProfile.department || userProfile.specialties) && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                    Work Information
                  </h4>
                  
                  {userProfile.title && (
                    <div className="flex items-center space-x-3 text-gray-600">
                      <Briefcase className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Role</p>
                        <p className="text-sm">{userProfile.title}</p>
                      </div>
                    </div>
                  )}

                  {userProfile.specialties && userProfile.specialties.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-900 mb-2">Specialties</p>
                      <div className="flex flex-wrap gap-2">
                        {userProfile.specialties.map((specialty, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Member Since */}
              {userProfile.created_at && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3 text-gray-600">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Member Since</p>
                      <p className="text-sm">{formatDate(userProfile.created_at)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Bio */}
              {userProfile.bio && (
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-2">
                    About
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {userProfile.bio}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500">No profile information available</p>
            </div>
          )}

          {/* Close Button */}
          <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;